import { FC, useEffect, useMemo } from 'react';

import * as Yup from 'yup'
import {Formik, Form, FormikProps, FormikValues} from 'formik'

import { 
  StatusErrorMessage, 
  billingDetailsSchema, FormikBillingDetails, initBillingDetails, ParseBillingFields, BillMode,
  FormikCheckBox, FormikFileUpload, FormikInspectorAgreeCheck, FormikDateSelector, 
  FormikNotesArea 
} from '../../../../../components/widgets';
import { doFormSubmit, isPopulatedArray, processNoteValues, reloadDraftVars } from '../../../../../components/helpers';
import { PhaseFormObserver } from '../../components/PhaseFormObserver';
import { cloneDeep } from 'lodash';
import { Application } from '../../../../core/applications';
import { SubmitButtonRow } from '../../../../../components/layout';

import { useQueryClient } from 'react-query';

const inspectorInspectionSchema = Yup.object({
  verdict: Yup.string().trim(),
  approved: Yup.boolean(),
  reviewId: Yup.number(),  

  attachmentId: Yup.number().integer().label('Completed Audit Kit')
  .when(['approved', 'state'], {
    is: (approved: boolean, state : string) => approved && state === 'Inspection - Inspection Kit',
    then: (schema) => schema.integer().moreThan(0, 'Completed Audit Kit required').required().label('Completed Audit Kit'),
  }),
  attachmentIdAttachmentName: Yup.string().trim(),
  
  cocAttachmentId: Yup.number().integer().label('Completed Draft COC')
  .when(['approved', 'state'], {
    is: (approved: boolean, state : string) => approved && state === 'Inspection - CoC',
    then: (schema) => schema.integer().moreThan(0, 'Completed Draft COC required').required().label('Completed Draft COC'),
  }),
  cocAttachmentIdAttachmentName: Yup.string().trim(),  
  rescheduleDate: Yup.string().trim().label('Reschedule Date')
  .when("verdict", {
    is: (verdict: string) => (verdict === 'reskedSampling'),
    then: (schema) => schema.required('Reschedule Date required.'),
  }),
  note: Yup.string().trim().label('Notes')
  .when("verdict", {
    is: (verdict: string) => (!['approved','reskedSampling'].includes(verdict)),
    then: (schema) => schema.required('Details required.').label('Notes'),
  }),
  noteAttachments: Yup.array(),
  inspectorAgree: Yup.bool().label('Confirmation')
  .when("approved", {
    is: true,
    then: (schema) => schema.oneOf([true], 'Must be checked'),
  }),
}).concat(billingDetailsSchema);


const STATE_BILL_MODES: Record<string, BillMode> = {
  'Inspection - CoC': 'NO',
  'Inspection - Inspection Kit': 'OPTIONAL',
};

function getBillMode(state: string, verdict: string): BillMode {
  // 1. Verdict check overrides state for non-approved/rejected outcomes
  if (!['approved', 'rejected'].includes(verdict)) {
    return 'NO';
  }

  // 2. Fall back to state lookup, default to 'YES'
  return STATE_BILL_MODES[state] ?? 'YES';
}

type FormProps = {
  formikProps: FormikProps<any>
  verdict: string
  doClose: Function
}

const InspectorInspectionComponentForm =( {formikProps, verdict, doClose}: FormProps) => {

  const {isSubmitting, values, status, setFieldValue, validateForm} = formikProps;

  const { reviewId, approved, state, verdict: verdictValue } = values;
  
  useEffect(() => {        
    
    setFieldValue('verdict', verdict);
    setFieldValue('approved', verdict === 'approved', false);

    const billMode = getBillMode(state, verdict);            
    setFieldValue('billingRequired', billMode);
  
    // Functional updater receives the current values safely
    setFieldValue('transports', (prevValues: any) => {
      const transports = prevValues?.transports ?? [];
      
      if (isPopulatedArray(transports)) {
        return transports.map((o: any) => ({ ...o, billingRequired: billMode }));
      }
      
      return transports;
    });
        
  }, [verdict, state, setFieldValue]);

  useEffect(() => {
    validateForm();
  }, [approved, state, verdictValue, validateForm]);

  return (
  <>
  <PhaseFormObserver reviewId={reviewId} values={values} isSubmitting={isSubmitting} verdict={verdict} />
  <Form id='kt_inspector_inspection_form' className='form' noValidate>
    <div
      className='d-flex flex-column gap-4 mb-6 px-2'
      id='kt_inspector_inspection_scroll'
      data-kt-scroll='true'
      data-kt-scroll-activate='{default: false, lg: true}'
      data-kt-scroll-max-height='auto'
      data-kt-scroll-dependencies='#kt_inspector_inspection_header'
      data-kt-scroll-wrappers='#kt_inspector_inspection_scroll'
      data-kt-scroll-offset='300px'
    >
      
      { verdict === 'approved' &&
      <div className='row mb-2'>
        <FormikCheckBox size="6" name="majorNCFound" label="Major Non-Conformity Found" />
      </div>
      }
            
      { verdict === 'approved' &&
      <>
      { (state === 'Inspection - Inspection Kit') &&
      <div className="row mb-2">
        <FormikFileUpload size="12" label="Completed Audit Kit" name='attachmentId' acceptFiles="UserFileFormats" required />
      </div>
      }      
      { (state === 'Inspection - CoC') &&
      <div className="row mb-2">
        <FormikFileUpload size="12" label="Completed Draft COC" name='cocAttachmentId' acceptFiles="UserFileFormats" required />
      </div>
      }      
      </>
      }
      
      <FormikBillingDetails />
      
      { (verdict === 'reskedSampling') && 
      <div className="row mb-2">
        <FormikDateSelector size="12" label="Reschedule Date" name="rescheduleDate" required={true} showShiftSelector={true} dateFormat='LL' asRow />
      </div>
      }
    
      { verdict === 'adminReport' &&
      <div className="row mb-2">
        <FormikFileUpload size="12" label="Attachment" name='attachmentId' acceptFiles="UserFileFormats" />
      </div>
      }      

      <FormikNotesArea label={(['approved','reskedSampling'].includes(verdict)) ? "Note" : "Reason"} required={!['approved','reskedSampling'].includes(verdict)} />
            
      { verdict === 'approved' &&
      <FormikInspectorAgreeCheck />
      }
      
      <StatusErrorMessage status={status} />
      <SubmitButtonRow doClose={doClose} />
    </div>
  </Form>
  </>
  )
}

type Props = {
  appData: Application
  reviewId: number
  reviewState: string
  draftVars: Record<string, any>
  verdict: string  
  doClose: Function
}

const InspectorInspectionForm : FC<Props> = ({ appData, reviewId, reviewState, draftVars, verdict, doClose }) => {

  const queryClient = useQueryClient();

  const initValues = useMemo(() => {

    let initValues = {
      verdict: '',
      approved: false,
      reviewId,
      state: reviewState,

      attachmentId: 0,
      attachmentIdAttachmentName: '',      
      cocAttachmentId: 0,
      cocAttachmentIdAttachmentName: '',
      majorNCFound: false,      
      inspectorAgree: false,      
      rescheduleDate: '',      
      ...cloneDeep(initBillingDetails),
      note: '',
      noteAttachments:[],
    };

    reloadDraftVars(initValues, draftVars, ['verdict','reviewId','approved','state','inspectorAgree']);

    return initValues;

  }, [reviewId, reviewState, draftVars]);


  const doSubmit = async (values: any, actions: FormikValues) => {
    const { setStatus, setSubmitting } = actions;
    // exclude empty fields, attachment names and booleans

    let approved = (verdict === 'approved') ? true : false;
    let payload = {      
      reviewId,
      approved, 
      majorNCFound: false
    };

    processNoteValues(values, payload);

    if (approved && values?.majorNCFound) {
      if (! window.confirm("Major Non-Conformity Found! Do you still want to proceed?")) {
        setSubmitting(false);
        return;
      }
      Object.assign(payload, { majorNCFound: values.majorNCFound });
    }

    if (approved && reviewState === 'Inspection - Inspection Kit') {
      Object.assign(payload, { attachmentId: values.attachmentId}); 
    }

    if (approved && reviewState === 'Inspection - CoC') {
      Object.assign(payload, { cocAttachmentId: values.cocAttachmentId}); 
    }

    if (verdict === 'reskedSampling') {
      Object.assign(payload, { 
        rescheduleDate: values.rescheduleDate,
        reschedule: true 
      });        
    }
  
    if (verdict === 'adminReport' && values?.attachmentId) {
      Object.assign(payload, { attachmentId: values.attachmentId}); 
    }

    payload = ParseBillingFields(payload, values);

    await doFormSubmit('Phase_5_2Servlet', payload, setStatus, setSubmitting, () => {
      queryClient.invalidateQueries(['APPLICATIONS-LIST']);
      queryClient.invalidateQueries(['GETAPPLICATION', `${appData.id}`]);
      doClose();
    },    
  );
  }

  return (
  <Formik
    initialValues={initValues}
    validationSchema={inspectorInspectionSchema}
    enableReinitialize={true}
    onSubmit={doSubmit}
  >
    {(formikProps) => (
    <InspectorInspectionComponentForm formikProps={formikProps} verdict={verdict} doClose={doClose} />
    )}
  </Formik>
  )
}


export {InspectorInspectionForm}
